import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  KeyRound, Plus, Trash2, Edit, RotateCcw, AlertTriangle,
  PackageCheck, UserCheck, Wrench, Copy, Building, History, Clock, User as UserIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { keysInventoryAPI, propertiesAPI, clientsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const STATUS_BADGES = {
  in_stock: { label: 'En stock', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: PackageCheck },
  with_tenant: { label: 'Avec locataire', cls: 'bg-blue-100 text-blue-800 border-blue-200', icon: UserCheck },
  with_artisan: { label: 'Avec artisan', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Wrench },
  returned: { label: 'Retournee', cls: 'bg-gray-100 text-gray-800 border-gray-200', icon: RotateCcw },
  lost: { label: 'Perdue', cls: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
  duplicated: { label: 'Dupliquee', cls: 'bg-purple-100 text-purple-800 border-purple-200', icon: Copy },
};

/**
 * KeysInventoryTab - Composant reutilisable pour gerer le stock de cles.
 * Props:
 *  - propertyId  : si fourni, n'affiche que les cles de ce bien (et propose la creation)
 *  - clientId    : si fourni, affiche les cles de TOUS les biens de ce client (proprietaire)
 *                  (lecture seule pour la creation : on indique de creer depuis le bien)
 *  - readOnly    : desactive les actions (defaut false)
 */
export const KeysInventoryTab = ({ propertyId, clientId, readOnly = false }) => {
  const { user } = useAuth();
  const isAdmin = user && (['admin', 'super_admin', 'agent'].includes(user.role));
  const [meta, setMeta] = useState({ types: [], statuses: [] });
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pour mode propertyId : on charge les units du bien
  const [units, setUnits] = useState([]);
  // Pour mode clientId : on charge les biens du proprio
  const [clientProperties, setClientProperties] = useState([]);

  // Modale create/edit
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    property_id: propertyId || '',
    unit_id: '',
    key_type: 'principale',
    reference: '',
    label: '',
    notes: '',
  });

  // Modale changement de statut
  const [showStatus, setShowStatus] = useState(false);
  const [statusKey, setStatusKey] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: 'returned', tenant_id: '', artisan_name: '', note: '',
  });
  const [tenants, setTenants] = useState([]);

  // Modale timeline
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineKey, setTimelineKey] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const openTimeline = async (k) => {
    setTimelineKey(k);
    setShowTimeline(true);
    setLoadingTimeline(true);
    try {
      const r = await keysInventoryAPI.getTimeline(k.id);
      setTimelineEvents(r.data?.events || []);
    } catch (e) {
      toast.error('Erreur chargement timeline');
      setTimelineEvents([]);
    }
    setLoadingTimeline(false);
  };

  const loadMeta = useCallback(async () => {
    try {
      const r = await keysInventoryAPI.getMeta();
      setMeta(r.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      let r;
      if (propertyId) r = await keysInventoryAPI.getByProperty(propertyId);
      else if (clientId) r = await keysInventoryAPI.getByClient(clientId);
      else r = await keysInventoryAPI.getAll();
      setKeys(r.data || []);
    } catch (e) {
      toast.error('Erreur chargement cles');
    }
    setLoading(false);
  }, [propertyId, clientId]);

  const loadUnits = useCallback(async () => {
    if (!propertyId) return;
    try {
      const r = await propertiesAPI.getUnits(propertyId);
      setUnits(r.data || []);
    } catch (e) { console.error(e); }
  }, [propertyId]);

  const loadClientProperties = useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await propertiesAPI.getAll({ client_id: clientId });
      setClientProperties(r.data || []);
    } catch (e) { console.error(e); }
  }, [clientId]);

  const loadTenants = useCallback(async () => {
    try {
      const r = await clientsAPI.getAll({ client_type: 'locataire' });
      setTenants(r.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadKeys(); }, [loadKeys]);
  useEffect(() => { loadUnits(); }, [loadUnits]);
  useEffect(() => { loadClientProperties(); }, [loadClientProperties]);

  const stats = useMemo(() => {
    const s = { total: keys.length };
    Object.keys(STATUS_BADGES).forEach((k) => { s[k] = keys.filter((x) => x.status === k).length; });
    return s;
  }, [keys]);

  const handleOpenCreate = async () => {
    setEditing(null);
    let nextRef = '';
    const targetProp = propertyId || (clientProperties[0]?.id || '');
    if (targetProp) {
      try {
        const r = await keysInventoryAPI.getNextReference(targetProp);
        nextRef = r.data.reference || '';
      } catch (e) { /* pass */ }
    }
    setForm({
      property_id: targetProp,
      unit_id: '',
      key_type: 'principale',
      reference: nextRef,
      label: '',
      notes: '',
    });
    setShowForm(true);
  };

  const handleOpenEdit = (k) => {
    setEditing(k);
    setForm({
      property_id: k.property_id,
      unit_id: k.unit_id || '',
      key_type: k.key_type,
      reference: k.reference,
      label: k.label || '',
      notes: k.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.property_id) { toast.error('Bien obligatoire'); return; }
    if (!form.key_type) { toast.error('Type de cle obligatoire'); return; }
    try {
      if (editing) {
        await keysInventoryAPI.update(editing.id, {
          unit_id: form.unit_id || null,
          unit_name: form.unit_id ? (units.find((u) => u.id === form.unit_id)?.unit_name || null) : null,
          key_type: form.key_type,
          reference: form.reference || null,
          label: form.label || null,
          notes: form.notes || null,
        });
        toast.success('Cle modifiee');
      } else {
        await keysInventoryAPI.create({
          property_id: form.property_id,
          unit_id: form.unit_id || null,
          unit_name: form.unit_id ? (units.find((u) => u.id === form.unit_id)?.unit_name || null) : null,
          key_type: form.key_type,
          reference: form.reference || null,
          label: form.label || null,
          notes: form.notes || null,
        });
        toast.success('Cle creee');
      }
      setShowForm(false);
      await loadKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (k) => {
    if (!window.confirm(`Supprimer la cle ${k.reference} ?`)) return;
    try {
      await keysInventoryAPI.delete(k.id);
      toast.success('Cle supprimee');
      await loadKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const openStatusChange = (k) => {
    setStatusKey(k);
    setStatusForm({ status: 'returned', tenant_id: '', artisan_name: '', note: '' });
    setShowStatus(true);
    if (!tenants.length) loadTenants();
  };

  const submitStatus = async () => {
    try {
      const payload = { status: statusForm.status, note: statusForm.note || null };
      if (statusForm.status === 'with_tenant') payload.tenant_id = statusForm.tenant_id || null;
      if (statusForm.status === 'with_artisan') payload.artisan_name = statusForm.artisan_name || null;
      await keysInventoryAPI.changeStatus(statusKey.id, payload);
      toast.success('Statut mis a jour');
      setShowStatus(false);
      await loadKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const propertyOptions = propertyId ? [] : clientProperties;
  const showCreateBtn = !readOnly && (propertyId || (clientId && clientProperties.length > 0));

  return (
    <div className="space-y-3" data-testid="keys-inventory-tab">
      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
        <div className="bg-[#1B3A4B] text-white rounded p-2 text-center">
          <div className="text-xs uppercase opacity-80">Total</div>
          <div className="text-lg font-bold">{stats.total}</div>
        </div>
        {Object.entries(STATUS_BADGES).map(([k, b]) => (
          <div key={k} className={`${b.cls} rounded p-2 text-center border`}>
            <div className="text-[10px] uppercase font-medium">{b.label}</div>
            <div className="text-lg font-bold">{stats[k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <KeyRound className="h-4 w-4 inline mr-1 text-[#D4A745]" />
          {keys.length} cle(s)
          {clientId && clientProperties.length > 0 && ` reparties sur ${clientProperties.length} bien(s)`}
        </div>
        {showCreateBtn && (
          <Button size="sm" onClick={handleOpenCreate}
                  className="bg-[#1B3A4B] hover:bg-[#0d2937] text-white"
                  data-testid="add-key-btn">
            <Plus className="h-4 w-4 mr-1" /> Ajouter une cle
          </Button>
        )}
      </div>

      {/* List */}
      <div className="rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[#1B3A4B]">
            <tr>
              <th className="text-left p-2">Reference</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Appartement</th>
              {clientId && <th className="text-left p-2">Bien</th>}
              <th className="text-left p-2">Statut</th>
              <th className="text-left p-2">Detenteur</th>
              {!readOnly && <th className="text-right p-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-4 text-center text-gray-400">Chargement...</td></tr>
            )}
            {!loading && keys.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">
                Aucune cle enregistree.
                {showCreateBtn && ' Cliquez sur "Ajouter une cle" pour commencer.'}
                {clientId && clientProperties.length === 0 && ' Ce client n\'a aucun bien associe.'}
              </td></tr>
            )}
            {keys.map((k) => {
              const Sb = STATUS_BADGES[k.status] || STATUS_BADGES.in_stock;
              const SIcon = Sb.icon;
              return (
                <tr key={k.id} className="border-t hover:bg-gray-50" data-testid={`key-row-${k.id}`}>
                  <td className="p-2 font-mono text-xs font-semibold">{k.reference}</td>
                  <td className="p-2">{k.key_type_label}{k.label ? ` - ${k.label}` : ''}</td>
                  <td className="p-2">{k.unit_name || <span className="text-xs text-gray-400">-</span>}</td>
                  {clientId && <td className="p-2 text-xs">{k.property_title}</td>}
                  <td className="p-2">
                    <Badge className={`${Sb.cls} border`}>
                      <SIcon className="h-3 w-3 mr-1 inline" />{Sb.label}
                    </Badge>
                  </td>
                  <td className="p-2 text-xs text-gray-600">
                    {k.status === 'with_tenant' && k.current_tenant_name}
                    {k.status === 'with_artisan' && k.current_artisan_name}
                    {!['with_tenant', 'with_artisan'].includes(k.status) && '-'}
                  </td>
                  {!readOnly && (
                    <td className="p-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost"
                                onClick={() => openTimeline(k)}
                                title="Historique"
                                data-testid={`timeline-${k.id}`}
                                className="text-[#1B3A4B]">
                          <History className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost"
                                onClick={() => openStatusChange(k)}
                                title="Changer statut"
                                data-testid={`change-status-${k.id}`}
                                className="text-blue-600">
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button size="sm" variant="ghost"
                                    onClick={() => handleOpenEdit(k)}
                                    data-testid={`edit-key-${k.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost"
                                    onClick={() => handleDelete(k)}
                                    data-testid={`delete-key-${k.id}`}
                                    className="text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="key-form-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B3A4B] flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[#D4A745]" />
              {editing ? 'Modifier la cle' : 'Ajouter une cle au stock'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!propertyId && propertyOptions.length > 0 && !editing && (
              <div>
                <Label>Bien *</Label>
                <Select value={form.property_id}
                        onValueChange={async (v) => {
                          setForm({ ...form, property_id: v, unit_id: '' });
                          try {
                            const r = await keysInventoryAPI.getNextReference(v);
                            setForm((f) => ({ ...f, property_id: v, unit_id: '', reference: r.data.reference }));
                          } catch (e) { /* pass */ }
                        }}>
                  <SelectTrigger><SelectValue placeholder="Choisir un bien..." /></SelectTrigger>
                  <SelectContent>
                    {propertyOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Reference *</Label>
                <Input value={form.reference}
                       onChange={(e) => setForm({ ...form, reference: e.target.value })}
                       placeholder="Ex: CLE-001"
                       data-testid="key-reference-input" />
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={form.key_type}
                        onValueChange={(v) => setForm({ ...form, key_type: v })}>
                  <SelectTrigger data-testid="key-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.types.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Appartement / Unite (lien obligatoire pour le suivi)</Label>
              {(propertyId || form.property_id) && units.length === 0 && (
                <div className="text-xs text-gray-500 italic mt-1">
                  Aucune unite definie sur ce bien (vous pouvez quand meme creer la cle).
                </div>
              )}
              <Select value={form.unit_id || 'none'}
                      onValueChange={(v) => setForm({ ...form, unit_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="key-unit-select"><SelectValue placeholder="Optionnel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun appartement specifique —</SelectItem>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.unit_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Libelle (facultatif)</Label>
              <Input value={form.label}
                     onChange={(e) => setForm({ ...form, label: e.target.value })}
                     placeholder="Ex: Cle bleue, Modele Vachette V5"
                     data-testid="key-label-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
            <Button onClick={handleSave}
                    data-testid="submit-key-btn"
                    className="bg-[#1B3A4B] hover:bg-[#0d2937] text-white">
              {editing ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog open={showStatus} onOpenChange={setShowStatus}>
        <DialogContent className="max-w-md" data-testid="key-status-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B3A4B]">
              Changer le statut - {statusKey?.reference}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nouveau statut *</Label>
              <Select value={statusForm.status}
                      onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                <SelectTrigger data-testid="new-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.statuses.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {statusForm.status === 'with_tenant' && (
              <div>
                <Label>Locataire</Label>
                <Select value={statusForm.tenant_id || 'none'}
                        onValueChange={(v) => setStatusForm({ ...statusForm, tenant_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Non specifie —</SelectItem>
                    {tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {statusForm.status === 'with_artisan' && (
              <div>
                <Label>Nom de l'artisan</Label>
                <Input value={statusForm.artisan_name}
                       onChange={(e) => setStatusForm({ ...statusForm, artisan_name: e.target.value })} />
              </div>
            )}
            <div>
              <Label>Note (facultatif)</Label>
              <Textarea rows={2} value={statusForm.note}
                        onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatus(false)}>Annuler</Button>
            <Button onClick={submitStatus}
                    data-testid="confirm-status-btn"
                    className="bg-[#1B3A4B] hover:bg-[#0d2937] text-white">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Modal */}
      <Dialog open={showTimeline} onOpenChange={setShowTimeline}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="key-timeline-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B3A4B] flex items-center gap-2">
              <History className="h-5 w-5 text-[#D4A745]" />
              Historique - {timelineKey?.reference}
            </DialogTitle>
          </DialogHeader>
          {timelineKey && (
            <div className="space-y-1 text-sm border-l-2 border-l-[#D4A745]/30 ml-2 pl-4 py-2">
              <p className="text-xs text-gray-500 mb-3">
                {timelineKey.key_type_label}
                {timelineKey.label ? ` - ${timelineKey.label}` : ''}
                {timelineKey.unit_name ? ` - ${timelineKey.unit_name}` : ''}
                {timelineKey.property_title ? ` - ${timelineKey.property_title}` : ''}
              </p>
              {loadingTimeline && <div className="text-xs text-gray-500">Chargement...</div>}
              {!loadingTimeline && timelineEvents.length === 0 && (
                <div className="text-xs text-gray-500 italic">Aucun evenement enregistre.</div>
              )}
              {!loadingTimeline && timelineEvents.map((ev, idx) => {
                const Sb = STATUS_BADGES[ev.to_status] || null;
                const SIcon = Sb ? Sb.icon : Clock;
                return (
                  <div key={idx} className="relative pb-4" data-testid={`timeline-event-${idx}`}>
                    <div className={`absolute -left-[27px] top-0 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white shadow ${
                      ev.type === 'created' ? 'bg-emerald-500' : Sb?.cls || 'bg-gray-200'
                    }`}>
                      <SIcon className="w-3 h-3 text-white" />
                    </div>
                    <div className="text-xs text-gray-500">
                      {ev.at ? new Date(ev.at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '-'}
                    </div>
                    <div className="font-medium text-[#1B3A4B]">{ev.title}</div>
                    {ev.subtitle && <div className="text-xs text-gray-700">{ev.subtitle}</div>}
                    {ev.by && (
                      <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                        <UserIcon className="h-3 w-3" /> par {ev.by}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeline(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KeysInventoryTab;
